import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { NgControl } from '@angular/forms';

export interface PhoneFormatConfig {
  maxDigits?: number;
  formatStyle?: 'nanp' | 'international';
  groupPatterns?: number[][];
}

@Directive({
  selector: '[appFormatPhone]'
})
export class FormatPhoneDirective {
  @Input('appFormatPhone') formatConfig?: PhoneFormatConfig | string;

  constructor(private el: ElementRef, private control: NgControl) {}

  @HostListener('input', ['$event'])
  onInput(event: KeyboardEvent) {
    let value: string = this.el.nativeElement.value;
    value = value.replace(/\D/g, '');

    const config = this.resolveConfig();
    if (this.hasGroupPatternData(config.groupPatterns)) {
      value = value.slice(0, config.maxDigits);
    }
    value = this.formatValue(value, config);

    this.el.nativeElement.value = value;
    if (this.control) {
      this.control.control?.setValue(value, { emitEvent: false });
    }
  }

  private resolveConfig(): PhoneFormatConfig {
    if (this.formatConfig && typeof this.formatConfig === 'object') {
      return {
        maxDigits: this.formatConfig.maxDigits ?? 10,
        formatStyle: this.formatConfig.formatStyle ?? 'nanp',
        groupPatterns: this.formatConfig.groupPatterns === undefined
          ? [[3, 3, 4]]
          : this.normalizeGroupPatterns(this.formatConfig.groupPatterns)
      };
    }

    return {
      maxDigits: 10,
      formatStyle: 'nanp',
      groupPatterns: [[3, 3, 4]]
    };
  }

  private formatValue(value: string, config: PhoneFormatConfig): string {
    if (!value) {
      return value;
    }

    const selectedPattern = this.getPatternForValue(value, config.groupPatterns);
    if (!selectedPattern) {
      return value;
    }

    if (config.formatStyle === 'nanp') {
      if (value.length > 3 && value.length <= 6) {
        return value.replace(/^(\d{3})(\d+)/, '($1) $2');
      }
      if (value.length > 6) {
        return value.replace(/^(\d{3})(\d{3})(\d+)/, '($1) $2-$3');
      }
      return value;
    }

    return this.groupDigits(value, selectedPattern);
  }

  private normalizeGroupPatterns(groupPatterns: number[][] | undefined): number[][] {
    const validPatterns = (groupPatterns || []).filter((pattern) => Array.isArray(pattern) && pattern.length);
    return validPatterns;
  }

  private hasGroupPatternData(groupPatterns: number[][] | undefined): boolean {
    return this.normalizeGroupPatterns(groupPatterns).length > 0;
  }

  private getPatternForValue(value: string, groupPatterns: number[][] | undefined): number[] | null {
    const validPatterns = this.normalizeGroupPatterns(groupPatterns);
    if (!validPatterns.length) {
      return null;
    }

    return validPatterns.find((pattern) => this.getPatternDigitCount(pattern) >= value.length) ||
      validPatterns[validPatterns.length - 1];
  }

  private getPatternDigitCount(pattern: number[]): number {
    return pattern.reduce((total, groupSize) => total + groupSize, 0);
  }

  private groupDigits(value: string, selectedPattern: number[]): string {
    const parts: string[] = [];
    let currentIndex = 0;

    selectedPattern.forEach((groupSize) => {
      if (currentIndex >= value.length) {
        return;
      }

      const nextIndex = currentIndex + groupSize;
      parts.push(value.slice(currentIndex, nextIndex));
      currentIndex = nextIndex;
    });

    if (currentIndex < value.length) {
      parts.push(value.slice(currentIndex));
    }

    return parts.filter(Boolean).join(' ');
  }
}
